import * as React from "react";
import {
  SessionAttachment,
  getCurrentSheetSessionAttachment,
  attachSheetToSession,
  detachSheetFromSession,
} from "../utils/sessionManager";

interface SessionContextType {
  /** Current session attachment information, or null if not attached */
  sessionAttachment: SessionAttachment | null;

  /** Whether session info is loading */
  loading: boolean;

  /** Error message if there was a problem loading session info */
  error: string | null;

  /** Whether the current sheet is attached to a session */
  isAttached: boolean;

  /** Attach the current sheet to a session */
  attachToSession: (sessionId: string, sessionName: string) => Promise<boolean>;

  /** Detach the current sheet from its session */
  detachFromSession: () => Promise<boolean>;

  /** Refresh session info */
  refreshSessionInfo: () => Promise<void>;

  /** Active sheet name */
  activeSheetName: string | null;
}

// Create the context with a default value
const SessionContext = React.createContext<SessionContextType>({
  sessionAttachment: null,
  loading: false,
  error: null,
  isAttached: false,
  attachToSession: async () => false,
  detachFromSession: async () => false,
  refreshSessionInfo: async () => {},
  activeSheetName: null,
});

// Custom hook to use the session context
export const useSession = () => React.useContext(SessionContext);

interface SessionProviderProps {
  children: React.ReactNode;
}

export const SessionProvider: React.FC<SessionProviderProps> = ({ children }) => {
  const [sessionAttachment, setSessionAttachment] = React.useState<SessionAttachment | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);
  const [activeSheetName, setActiveSheetName] = React.useState<string | null>(null);

  // Get active sheet name
  const getActiveSheetName = React.useCallback(async () => {
    try {
      await Excel.run(async (context) => {
        const activeSheet = context.workbook.worksheets.getActiveWorksheet();
        activeSheet.load("name");
        await context.sync();

        setActiveSheetName(activeSheet.name);
      });
    } catch (err) {
      console.error("Error getting active sheet name:", err);
    }
  }, []);

  // Check if the current sheet is attached to a session
  const refreshSessionInfo = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // First, get the active sheet name
      await getActiveSheetName();

      // Then check if it's attached to a session
      const attachment = await getCurrentSheetSessionAttachment();
      setSessionAttachment(attachment);
    } catch (err) {
      console.error("Error checking session attachment:", err);
      setError("Failed to check session attachment");
    } finally {
      setLoading(false);
    }
  }, [getActiveSheetName]);

  // Subscribe to worksheet activation event
  React.useEffect(() => {
    let isSubscribed = true;

    const setupWorksheetListener = async () => {
      try {
        await Excel.run(async (context) => {
          // Set up event handler for worksheet activation
          context.workbook.worksheets.onActivated.add(async () => {
            if (isSubscribed) {
              await refreshSessionInfo();
            }
          });

          await context.sync();
          console.log("Worksheet activation listener set up");
        });
      } catch (error) {
        console.error("Error setting up worksheet activation listener:", error);
      }
    };

    setupWorksheetListener();

    return () => {
      isSubscribed = false;
    };
  }, [refreshSessionInfo]);

  // Check session on load
  React.useEffect(() => {
    refreshSessionInfo();
  }, [refreshSessionInfo]);

  // Attach to session
  const attachToSession = React.useCallback(
    async (sessionId: string, sessionName: string) => {
      setLoading(true);
      setError(null);

      try {
        const success = await attachSheetToSession(sessionId, sessionName);

        if (success) {
          // Refresh session info after attachment
          await refreshSessionInfo();
        }

        return success;
      } catch (err) {
        console.error("Error attaching to session:", err);
        setError("Failed to attach to session");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [refreshSessionInfo]
  );

  // Detach from session
  const detachFromSession = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const success = await detachSheetFromSession();

      if (success) {
        // Clear session attachment
        setSessionAttachment(null);
      }

      return success;
    } catch (err) {
      console.error("Error detaching from session:", err);
      setError("Failed to detach from session");
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  // Value provided by the context
  const value = React.useMemo(
    () => ({
      sessionAttachment,
      loading,
      error,
      isAttached: !!sessionAttachment,
      attachToSession,
      detachFromSession,
      refreshSessionInfo,
      activeSheetName,
    }),
    [sessionAttachment, loading, error, attachToSession, detachFromSession, refreshSessionInfo, activeSheetName]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};
